import {useEffect} from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  nextPage,
  previousPage,
  questionAnswered,
  selectAdaptabilityQuestions,
  resetPage,
  selectPage,
  selectAdaptabilityComplete
} from "../features/adaptability/adaptabilitySlice.js";
import adaptabiltyIcon from "../assets/Navigation/nav_icon_adaptability_green.png";
import SideNav from "../components/SideNav";
import PageTitle from "../components/PageTitle";
import { Link } from "react-router-dom";
import SlideOut from "../components/SlideOut.js";
import remoteMonitoringChart from '../assets/Popups/popup_remote_monitoring_graph.jpg'
import meterGraphic from '../assets/Popups/popup_meter_image.jpg'
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { Button, TextField, Container, Grid } from "@mui/material";

const Adaptability = () => {
  const page = useSelector(selectPage);
  const questions = useSelector(selectAdaptabilityQuestions)
  const dispatch = useDispatch();
  const section = {
    title: "Adaptabilidad y Escalabilidad",
    icon: adaptabiltyIcon,
    pages: [1, 2, 3],
  };

  const isComplete = useSelector(selectAdaptabilityComplete)
  useEffect(
    ()=>!isComplete ? dispatch(resetPage()):null,[]
  )

  const remoteMonitoring = {
    title: 'Reduzca la dependencia de personal',
    copy: 'Los caudalímetros multifásicos Roxar 2600 proporcionan datos de acceso remoto para informar de las dependencias de personal a medida que sus operaciones fluctúan:',
    bullets: [<li>Reducción de la dependencia de las adiciones/reducciones de personal a medida que la operación crece/se reduce</li>],
    view:{
      vert:true,
      copy:['Ver software:',<br/>,'Monitoreo remoto'],
      paragraph:['Reduzca la dependencia del personal aprovechando las capacidades de acceso remoto y la información operativa que ofrece el caudalímetro multifásico Roxar 2600.'],
      graphic: remoteMonitoringChart
    }
  }

  const meterConfiguration = {
    title: 'Configuración adaptable del medidor',
    copy:'La configuración mecánica y de software del caudalímetro multifásico Roxar 2600 es adaptable al campo:',
    bullets: [<li>Configuración de software automatizada y continua que se adapta a regímenes de caudal rápidamente cambiantes.</li>,
    <li>La posibilidad de cambiar el venturi en el campo amplía el ámbito de funcionamiento de la tasa de caudal del medidor.</li>],
    view:{
      vert:false,
      copy:['Más información sobre',<br/>,'la configuración adaptable', <br />, 'del medidor'],
      paragraph:['Con la ágil arquitectura de software Rapid Adaptive Measurement, el MPFM Roxar 2600 selecciona automáticamente los métodos de cálculo óptimos en función del perfil de caudal que el medidor observa en cada momento.',
      <br />,
      <br />,
      'El diseño de venturi reemplazable permite el cambio en el campo a una garganta de venturi más estrecha que amplía la envolvente operativa, ya que las tasas de caudal suelen disminuir a lo largo de la vida útil del pozo.'],
      graphic: meterGraphic
    }
  }

  return (
    <Container className="content_container" maxWidth='lg'>
      <div className="roxar_content">
      <div className="main_content">
        <PageTitle section={section} currentPage={page} />
        <div className="page_contents">
          {page === 1 && (
            <div className="challenges">
              <div>
                <h1>Desafíos de producción</h1>
                <ul>
                  <li>
                  Limitación práctica del número de pozos/visitas por técnico de campo cuando se requiere la presencia física para identificar, solucionar y remediar problemas
                  </li>
                  <li>
                  El diseño y el dimensionamiento de los separadores se limitan a determinados rangos de caudal/perfiles de producción, lo que requiere revisiones importantes en entornos de producción cambiantes
                  </li>
                </ul>
              </div>
              <div className="solution_copy">
                <p>
                Optimizar la producción es el objetivo final de cualquier operación, pero las limitaciones prácticas, como la presencia física de los técnicos de campo para identificar, solucionar y remediar los problemas, suponen limitaciones. El diseño y el dimensionamiento de los separadores también pueden limitar la producción, lo que exige revisiones importantes para cambiar los entornos de producción. 
                </p>
                <Button className="forward_button production_button" onClick={() => dispatch(nextPage())}>Aprenda cómo</Button>
              </div>
            </div>
          )}
          {page === 2 && (
            <div className="questions">
                  <h3 className="question">P: ¿Con qué frecuencia visitan sus ingenieros de campo cada plataforma de pozos?</h3>
                  <div className="answers">
                      <Button className={`answer ${questions[0].answer=='Cada 1 a 2 semanas'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:0,answer:'Cada 1 a 2 semanas', score:1, max: 4}))} > Cada 1 a 2 semanas</Button>
                      <Button className={`answer ${questions[0].answer=='Cada 3 a 4 semanas'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:0,answer:'Cada 3 a 4 semanas', score:2, max: 4}))}> Cada 3 a 4 semanas</Button>
                      <Button className={`answer ${questions[0].answer=='Cada 5 a 6 semanas'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:0,answer:'Cada 5 a 6 semanas', score:3, max: 4}))}> Cada 5 a 6 semanas</Button>
                      <Button className={`answer ${questions[0].answer=='Cada 7 semanas o más'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:0,answer:'Cada 7 semanas o más', score:4, max: 4}))}> Cada 7 semanas o más</Button>
                  </div>
            
                  <h3 className="question">P: ¿Cuál es el número ideal de pozos que puede gestionar eficazmente cada ingeniero de campo?</h3>
                  <div className="answers short_answers">
                      <Button className={`answer ${questions[1].answer=='1'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:1,answer:'1', score:1, max: 5}))}>1</Button>
                      <Button className={`answer ${questions[1].answer=='2-5'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:1,answer:'2-5', score:2, max: 5}))}> 2-5</Button>
                      <Button className={`answer ${questions[1].answer=='6-10'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:1,answer:'6-10', score:3, max: 5}))}> 6-10</Button>
                <div></div>
                
                      <Button className={`answer ${questions[1].answer=='De 11 a 50'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:1,answer:'De 11 a 50', score:4, max: 5}))}> De 11 a 50</Button>
                      <Button className={`answer ${questions[1].answer=='Más de 50'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:1,answer:'Más de 50', score:5, max: 5}))}> Más de 50</Button>
                  </div>
              
              <div className="page_nav">
                  <Button className="prev_button" onClick={() => dispatch(previousPage())}><ArrowBackIos /><span>Anterior</span></Button>
                  <Button className="next_button" onClick={() => dispatch(nextPage())}><span>Siguiente</span><ArrowForwardIos /></Button>
              </div>
            </div>
          )}
          {page === 3 && (
            <div className="questions">
             
                  <h3 className="question">P: ¿Alguna vez ha tenido que reajustar o cambiar el equipo del pozo debido a cambios en los perfiles de producción, los caudales o la situación?</h3>
                  <div className="answers">
                      <Button className={`answer ${questions[2].answer=='Sí'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:2,answer:'Sí', score:2, max: 4}))}>Sí</Button>
                      <Button className={`answer ${questions[2].answer=='No'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:2,answer:'No', score:2, max: 4}))} >No</Button>
                  </div>
           
              
                  <div className="page_nav">
              <Button className="prev_button" onClick={() => dispatch(previousPage())}><ArrowBackIos /><span>Anterior</span></Button>
                  <Button className="next_button"  component={Link} to={'/data'}><span>Siguiente</span><ArrowForwardIos /></Button>
              </div>
              <div>
                  <Button component={Link} to='/results' className="next_button view_results">Ver los resultados de la sección</Button>
              </div>
            </div>
          )}
        </div>
      </div>
        {page === 2 && (
          <SlideOut title={remoteMonitoring.title} copy={remoteMonitoring.copy}  bullets={remoteMonitoring.bullets} view={remoteMonitoring.view}/>
          )}
          {page === 3 && (
            <SlideOut title={meterConfiguration.title} copy={meterConfiguration.copy}  bullets={meterConfiguration.bullets} view={meterConfiguration.view}/>
            )}
      <SideNav />
      </div>
    </Container>
  );
};

export default Adaptability;
